import { API_BASE } from './apiBase';

/*
 * r16o3 v3-backlog (2026-10-24): the pure parts of the two pages rebuilt from the legacy Thymeleaf UI - SMM > Subscriptions
 * (pages/SmmSubscriptionsPage.js, V1 /smmSubscriptionOrders/) and Growth > Inbuilt scraping (pages/InbuiltScrapingPage.js, the
 * inbuilt-scraping block of V1 /orderManagement/accountSettings). Nothing here decides a rule of its own: the states are read
 * from what the server sends (lib/r14Errors.js style), the grid checks are the server's (InbuiltScrapingGridCheck).
 */

/** A 404 / 405 with no JSON error of ours = a server without the route (before r16o3). */
export function routeMissing(err) {
  const status = err && err.response && err.response.status;
  if (status !== 404 && status !== 405) return false;
  const data = err.response.data;
  // our own 404s (an unknown order / row) carry { error: "<sentence>", ... } with done / deleted false
  if (data && typeof data === 'object' && (data.done === false || data.deleted === false || data.saved === false)) return false;
  return true;
}

/** The server's own words for a failed call (the axios interceptor also copies `error` / `message` into `detail`). */
export function serverError(err, fallback) {
  const d = err && err.response && err.response.data;
  const w = d && typeof d === 'object' ? (d.error || d.detail || d.message) : null;
  return (typeof w === 'string' && w.trim()) || (err && err.message) || fallback || 'unknown error';
}

/** Where the legacy V1 page lives: the API's own origin (V3 is served from another host in production). */
export function legacyUrl(path, apiBase = API_BASE) {
  try {
    if (/^https?:\/\//i.test(apiBase)) return new URL(apiBase).origin + path;
  } catch (e) { /* fall through */ }
  return path;
}

/*
 * A subscription order's boosting state. V1 stores no "paused" flag: the order's username in the boost list (setting 1305) of
 * each assigned slot IS the state, and the server counts it (boosting_slots of assigned_slots_known; unknown_slots = slots with
 * no Instagram account, or an order with no username). null counts = the server's boost-state switch is off.
 */
export function boostState(row) {
  const n = row ? row.assigned_slots_known : null;
  const k = row ? row.boosting_slots : null;
  const unknown = (row && row.unknown_slots) || 0;
  if (n === null || n === undefined || k === null || k === undefined) {
    return { kind: 'unknown', label: 'not read', canPause: true, canResume: true };
  }
  if (n === 0) return { kind: 'none', label: 'no assigned slot', canPause: false, canResume: false };
  const readable = n - unknown;
  if (readable <= 0) return { kind: 'unknown', label: `${n} slot${n === 1 ? '' : 's'}, state unknown`, canPause: true, canResume: true };
  if (k === 0) return { kind: 'paused', label: `paused on ${readable === 1 ? 'its slot' : `all ${readable} slots`}`, canPause: false, canResume: true };
  // Boosting on every slot the server can change: Resume would change nothing. A slot without an Instagram account (unknown)
  // is skipped by resume too (server SubscriptionOrderPause.apply), so it does not make Resume useful (review should-fix,
  // 2026-10-24: 94ec730 kept Resume on here).
  if (k >= readable) return { kind: 'boosting', label: `boosting on ${k === 1 ? 'its' : `all ${k}`} slot${k === 1 ? '' : 's'}`, canPause: true, canResume: false };
  return { kind: 'partly', label: `boosting on ${k} of ${readable} slots`, canPause: true, canResume: true };
}

/** "#12 on 3 of 3 slots" for the toast after a pause / resume. */
export function actionToast(action, orderId, res) {
  const verb = action === 'resume' ? 'Resumed' : 'Paused';
  if (res && res.boosting_slots !== undefined && res.boosting_slots !== null) {
    return `${verb} order #${orderId}: boosting on ${res.boosting_slots} of ${res.assigned_slots_known} assigned slot${res.assigned_slots_known === 1 ? '' : 's'}`;
  }
  return `${verb} order #${orderId}`;
}

/* ------------------------------------------------------------------ inbuilt scraping */

/** Lines of a pending grid the server would take (it trims the grid first). */
export function gridLines(grid) {
  const t = String(grid || '').trim();
  return t ? t.split('\n') : [];
}

export function issueCounts(issues) {
  const list = Array.isArray(issues) ? issues : [];
  const errors = list.filter((i) => i.severity === 'error').length;
  return { errors, warnings: list.length - errors };
}

/** What the page sends: only the keys that changed, plus the grid as loaded (the server refuses a stale one with 409). */
export function inbuiltSaveBody(loaded, form) {
  const body = {};
  if (!!form.enable !== !!loaded.enable_inbuilt_scraping_service) body.enable_inbuilt_scraping_service = !!form.enable;
  const before = String(loaded.inbuilt_scraping_settings_grid || '');
  if (String(form.grid || '').replace(/\r\n/g, '\n') !== before.replace(/\r\n/g, '\n')) {
    body.inbuilt_scraping_settings_grid = String(form.grid || '').replace(/\r\n/g, '\n');
    body.inbuilt_scraping_settings_grid_before = before;
  }
  return body;
}

/** "120 / 2,500" style progress for a stored row (scraped of amount), or null when the row has no columns. */
export function rowProgress(row) {
  if (!row || row.amount === undefined) return null;
  const amount = parseInt(row.amount, 10);
  const scraped = parseInt(row.scraped, 10);
  const assigned = parseInt(row.assigned, 10);
  if (!Number.isFinite(amount)) return null;
  return {
    amount,
    scraped: Number.isFinite(scraped) ? scraped : 0,
    assigned: Number.isFinite(assigned) ? assigned : 0,
    pct: amount > 0 && Number.isFinite(scraped) ? Math.min(100, Math.round((scraped / amount) * 100)) : 0,
  };
}
